import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  Zap, Globe, BarChart3, CreditCard, Sparkles, ArrowRight,
  MessageCircle, Smartphone, Wand2, Workflow, ChevronDown,
  Bot, ShoppingCart, Megaphone, Layers, Star, Check, Menu, X,
  Mic, Search, FileText, Image, Video, Volume2, Users, Settings,
  Target, TrendingUp, Headphones, Mail, Phone, Shield, Database,
  Play, ChevronRight, Circle, Rocket, Plug,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import FeatureExplainerVideo from '../components/landing/FeatureExplainerVideo';
import '../styles/landing-clean.css';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';
const PLAN_ORDER = ['basic', 'starter', 'growth'];

/* ─────────────────────────────────────────────────────────────────────────────
   Animation helpers
───────────────────────────────────────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

function AnimatedSection({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} className={className}>
      {children}
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Floating orbs
───────────────────────────────────────────────────────────────────────────── */
function FloatingOrbs() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <motion.div className="absolute w-[700px] h-[700px] rounded-full bg-white/30 blur-[120px]"
        style={{ top: '-18%', left: '-12%' }}
        animate={{ x: [0, 40, 0], y: [0, 24, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }} />
      <motion.div className="absolute w-[480px] h-[480px] rounded-full bg-[#ef9f00]/25 blur-[100px]"
        style={{ top: '30%', right: '-10%' }}
        animate={{ x: [0, -30, 0], y: [0, 36, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut', delay: 4 }} />
      <motion.div className="absolute w-[360px] h-[360px] rounded-full bg-white/25 blur-[90px]"
        style={{ bottom: '8%', left: '40%' }}
        animate={{ x: [0, 20, 0], y: [0, -24, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 8 }} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Animated counter
───────────────────────────────────────────────────────────────────────────── */
function Counter({ to, suffix = '', prefix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const timer = setInterval(() => {
      start += to / (1800 / 16);
      if (start >= to) { setCount(to); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, to]);
  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Typewriter
───────────────────────────────────────────────────────────────────────────── */
const HEADLINES = [
  'AI Agents that never sleep',
  'Lead Generation',
  'Website Building',
  'WhatsApp Marketing',
  'AI Automation',
  'White-label Reselling',
];
function TypewriterText() {
  const [idx, setIdx] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const target = HEADLINES[idx];
    let t;
    if (!deleting && displayed.length < target.length)
      t = setTimeout(() => setDisplayed(target.slice(0, displayed.length + 1)), 60);
    else if (!deleting && displayed.length === target.length)
      t = setTimeout(() => setDeleting(true), 2200);
    else if (deleting && displayed.length > 0)
      t = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 35);
    else { setDeleting(false); setIdx(i => (i + 1) % HEADLINES.length); }
    return () => clearTimeout(t);
  }, [displayed, deleting, idx]);

  const sleepIdx = displayed.toLowerCase().indexOf('never sleep');
  const highlightAgents = sleepIdx >= 0;
  const before = highlightAgents ? displayed.slice(0, sleepIdx) : displayed;
  const highlight = highlightAgents ? displayed.slice(sleepIdx) : '';

  return (
    <span>
      <span className="bg-gradient-to-r from-[#FF6B4A] via-[#FF8A65] to-[#14B8A6] bg-clip-text text-transparent">
        {before}
      </span>
      {highlight ? (
        <span className="ml-1 inline-block align-baseline bg-[#16130d] text-[#fbbf0f] px-2.5 py-0.5 rounded-md shadow-[0_3px_0_0_rgba(22,19,13,0.35)]">
          {highlight}
        </span>
      ) : null}
      <span className="animate-pulse text-[#FF6B4A]">|</span>
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PRODUCTS — exact match to dashboard nav
───────────────────────────────────────────────────────────────────────────── */
const buildProducts = (brand) => [
  {
    id: 'websites',
    icon: <Wand2 className="w-5 h-5" />,
    color: 'from-violet-600 to-indigo-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'Core',
    title: 'My Websites',
    subtitle: 'AI Website Builder',
    desc: `Build full-stack websites with AI. Use the step-by-step ${brand} Builder or start from a professionally designed ${brand} template.`,
    features: [
      `${brand} Builder — planner → spec → code`,
      `Create from ${brand} template library`,
      'Visual drag-and-drop editor',
      'AI Page Assistant & Site Brain',
      'Website preview & one-click publish',
      'Custom domain connection',
    ],
  },
  {
    id: 'snagbuilder',
    icon: <Rocket className="w-5 h-5" />,
    color: 'from-emerald-600 to-teal-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'New',
    title: 'Snag Builder',
    subtitle: 'AI Full-stack App Builder',
    desc: `${brand} Builder turns a single prompt into a full-stack website or app — it plans, specs, writes the code and shows you a live preview you can keep editing.`,
    features: [
      'Prompt → full working app',
      'AI planner → spec → code',
      'Live sandbox preview',
      'Iterate and refine with AI',
      'One-click deploy & publish',
      'Start fresh or bring your own code',
    ],
  },
  {
    id: 'ai-cofounder',
    icon: <Sparkles className="w-5 h-5" />,
    color: 'from-amber-500 to-yellow-600',
    glow: 'rgba(251,191,15,0.45)',
    tag: 'Never Sleeps',
    title: 'AI Co-founder',
    subtitle: 'AI agents that never sleep',
    desc: 'Your always-on co-founder — AI agents that plan, draft, and run growth loops across CRM, marketing, builder, SEO, and social while you sleep. You approve before anything goes live. Included on every plan with tiered autonomy.',
    features: [
      'AI agents that never sleep — 24/7 growth loops',
      'Included on Basic, Starter & Growth',
      'Basic: plan & draft · 1 cycle/day',
      'Starter: ≈10 cycles/day · real lead scrape',
      'Growth: always-on · multi-brand',
      'Approve before send / publish / deploy',
    ],
  },
  {
    id: 'ai-tools',
    icon: <Sparkles className="w-5 h-5" />,
    color: 'from-fuchsia-600 to-pink-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: '22 Tools',
    title: 'AI Tools',
    subtitle: '9 Categories · 22 Tools',
    desc: 'A complete AI creative suite built into your workspace — content, media, audio, video, bots, documents and more.',
    features: [
      'Content: Writer, Editor, ReWriter, Article Wizard',
      'Media: Image, Photo Studio, Product Photography, Avatar, Vision',
      'Audio: Voiceover, Speech to Text',
      'Video: AI Video Generator, AI YouTube',
      'Bots: Smart Bots, Voice Bot',
      'Docs: File Chat, Web Chat, RSS, Presentation',
    ],
  },
  {
    id: 'media-studio',
    icon: <Image className="w-5 h-5" />,
    color: 'from-fuchsia-600 to-purple-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'Creative',
    title: 'Media Studio',
    subtitle: 'AI Image · Video · Audio',
    desc: 'Generate on-brand images, product photography, videos, avatars and voiceovers from a single creative studio — no designer needed.',
    features: [
      'AI image & product photography',
      'AI video generator',
      'Voiceover & speech-to-text',
      'AI avatars & vision',
      'Brand-consistent assets',
      'Export in any format',
    ],
  },
  {
    id: 'bot-leads',
    icon: <Bot className="w-5 h-5" />,
    color: 'from-cyan-600 to-teal-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'Lead Capture',
    title: 'Bot Leads & Calls',
    subtitle: 'Smart Bots + Voice AI',
    desc: 'Deploy AI chatbots and voice bots on your website to capture leads, book appointments and handle inbound calls 24/7.',
    features: [
      'AI Smart Bots with custom knowledge base',
      'AI Voice Bot for inbound calls',
      'Automatic lead capture from conversations',
      'Calendar appointment booking',
      'Call transcripts & recordings',
      'Seamless CRM handoff',
    ],
  },
  {
    id: 'voice',
    icon: <Phone className="w-5 h-5" />,
    color: 'from-emerald-600 to-green-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'Outbound',
    title: 'Voice Campaigns',
    subtitle: 'AI Outbound Calling',
    desc: 'Build your AI voice bot and run automated outbound campaigns. Reach leads with personalised calls — one Voice module.',
    features: [
      'AI voice bot builder + outbound campaigns',
      'Multi-contact dialing with Twilio BYOK',
      'Real-time campaign status tracking',
      'Automatic call transcripts',
      'Lead qualification via voice',
      'Schedule campaigns for optimal times',
    ],
  },
  {
    id: 'domains',
    icon: <Globe className="w-5 h-5" />,
    color: 'from-blue-600 to-sky-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'Infrastructure',
    title: 'Domains & Hosting',
    subtitle: 'Domain Registration + Hosting',
    desc: `Search, register and manage custom domains for all your websites. Connect them instantly to your ${brand}-built sites.`,
    features: [
      'Domain search across all TLDs',
      'One-click domain registration',
      'My Domains management panel',
      'Hosting plans included',
      'Auto-connect to your websites',
      'SSL certificates included',
    ],
  },
  {
    id: 'marketing',
    icon: <Megaphone className="w-5 h-5" />,
    color: 'from-orange-600 to-amber-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'Multi-channel',
    title: 'Marketing Suite',
    subtitle: 'Email · SMS · WhatsApp',
    desc: 'Unified multi-channel marketing platform. Run campaigns, manage contacts and track results across Email, SMS and WhatsApp.',
    features: [
      'Email Marketing — campaigns & templates',
      'SMS Marketing — bulk messaging',
      'WhatsApp — broadcasts, chatbots, warmer',
      'WhatsApp contacts, chats & tickets',
      'Unified CRM inbox',
      'Campaign analytics & blacklist management',
    ],
  },
  {
    id: 'ai-seo',
    icon: <TrendingUp className="w-5 h-5" />,
    color: 'from-green-600 to-emerald-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'Growth',
    title: 'AI SEO',
    subtitle: 'Rank higher, on autopilot',
    desc: 'Research keywords, generate optimised content and run automated SEO campaigns that grow your organic traffic month after month.',
    features: [
      'Keyword research & clustering',
      'AI-optimised content generation',
      'On-page SEO audits & fixes',
      'Automated SEO campaigns',
      'Rank tracking & reporting',
      'Technical SEO recommendations',
    ],
  },
  {
    id: 'ai-ads',
    icon: <Target className="w-5 h-5" />,
    color: 'from-amber-600 to-orange-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'Paid',
    title: 'AI Ads',
    subtitle: 'AI-generated ad campaigns',
    desc: 'Create high-converting image and video ads, generate copy and launch campaigns across Meta and Google — all powered by AI.',
    features: [
      'AI image & video ad creatives',
      'Ad copy generation',
      'Multi-language ads',
      'Social scheduler & publishing',
      'Ready-made campaign templates',
      'Performance-ready exports',
    ],
  },
  {
    id: 'automations',
    icon: <Workflow className="w-5 h-5" />,
    color: 'from-rose-600 to-red-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'No-code',
    title: 'Automations',
    subtitle: 'Visual Workflow Builder',
    desc: 'Build powerful trigger-based automations without writing code. Connect your tools, set conditions and let workflows run.',
    features: [
      'Visual drag-and-drop workflow editor',
      'Trigger-based automation rules',
      'Connect any tool or API',
      'Conditional branching logic',
      'Schedule & time-based triggers',
      'Monitor runs in real time',
    ],
  },
  {
    id: 'ai-studio',
    icon: <Zap className="w-5 h-5" />,
    color: 'from-yellow-600 to-orange-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'Agents',
    title: 'AI Studio',
    subtitle: 'Custom AI Agents',
    desc: 'Build, deploy and monitor custom AI agents that run autonomously. Use pre-built templates or design your own from scratch.',
    features: [
      'Agent builder with full configuration',
      'Visual workflow node editor',
      'Live monitoring & agent logs',
      'Pre-built agent templates',
      'Connect to 100+ tools & APIs',
      'Deploy agents that run 24/7',
    ],
  },
  {
    id: 'lead-scraper',
    icon: <Search className="w-5 h-5" />,
    color: 'from-lime-600 to-green-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'Prospecting',
    title: 'Lead Scraper',
    subtitle: 'Multi-source Lead Generation',
    desc: 'Pull high-intent leads from Google Maps and business directories. Auto-enrich with contact details and export directly to your CRM with one click.',
    features: [
      'Google Maps business scraping',
      'Business directory scraping',
      'Auto-enrich with email & phone',
      'Filter by location, category, rating',
      'One-click CRM export',
      'Bulk download as CSV',
    ],
  },
  {
    id: 'modules',
    icon: <Database className="w-5 h-5" />,
    color: 'from-indigo-600 to-purple-600',
    glow: 'rgba(251,191,15,0.35)',
    tag: 'ERP',
    title: `${brand} Modules`,
    subtitle: 'Full Business ERP Backend',
    desc: `Access the full power of ${brand} ERP — e-commerce, CRM, inventory, accounting and 50+ business modules — all under your own brand.`,
    features: [
      'E-commerce & online store',
      'CRM & sales pipeline',
      'Inventory & warehouse management',
      'Accounting & invoicing',
      'HR & payroll modules',
      `50+ ${brand} business apps`,
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────────────────
   FLOW STEPS — the connected journey
───────────────────────────────────────────────────────────────────────────── */
const buildFlowSteps = (brand) => [
  {
    step: '01',
    icon: <Search className="w-6 h-6" />,
    color: 'from-lime-500 to-emerald-500',
    glow: 'rgba(251,191,15,0.45)',
    title: 'Find Your Leads',
    module: 'Lead Scraper + Bot Leads',
    desc: 'Scrape high-intent prospects from Google Maps and business directories. Deploy AI chatbots and voice bots on your site to capture inbound leads automatically.',
    tools: ['Lead Scraper', 'AI Smart Bots', 'Voice Campaigns'],
  },
  {
    step: '02',
    icon: <Megaphone className="w-6 h-6" />,
    color: 'from-orange-500 to-amber-500',
    glow: 'rgba(251,191,15,0.45)',
    title: 'Engage & Nurture',
    module: 'Marketing Suite',
    desc: 'Reach your leads across Email, SMS and WhatsApp with personalised campaigns. Use AI to write copy, warm your numbers and automate follow-ups.',
    tools: ['Email Marketing', 'SMS Marketing', 'WhatsApp Broadcasts', 'AI Writer', 'Brand Voice'],
  },
  {
    step: '03',
    icon: <Wand2 className="w-6 h-6" />,
    color: 'from-violet-500 to-indigo-500',
    glow: 'rgba(251,191,15,0.45)',
    title: 'Build Your Presence',
    module: 'Website Builder + AI Tools',
    desc: `Launch a production-ready website with AI. Generate images, product photos, videos and content — all from within ${brand}.`,
    tools: [`${brand} Builder`, 'Visual Editor', 'AI Image', 'AI Product Photography', 'AI Video', 'Domains'],
  },
  {
    step: '04',
    icon: <ShoppingCart className="w-6 h-6" />,
    color: 'from-cyan-500 to-blue-500',
    glow: 'rgba(251,191,15,0.45)',
    title: 'Convert & Sell',
    module: `${brand} Modules (ERP)`,
    desc: 'Turn visitors into paying customers with a full e-commerce stack — multi-currency checkout, CRM pipeline, inventory and invoicing.',
    tools: [`${brand} E-commerce`, 'CRM Pipeline', 'Inventory', 'Invoicing', 'Multi-currency Payments'],
  },
  {
    step: '05',
    icon: <Zap className="w-6 h-6" />,
    color: 'from-yellow-500 to-orange-500',
    glow: 'rgba(251,191,15,0.45)',
    title: 'Automate & Scale',
    module: 'AI Studio + Automations',
    desc: 'Build custom AI agents and no-code workflows that run every repeating task for you — 24/7, without supervision.',
    tools: ['AI Agents', 'Visual Workflows', 'Trigger Automations', 'AI Monitoring', 'Agent Templates'],
  },
  {
    step: '06',
    icon: <Shield className="w-6 h-6" />,
    color: 'from-fuchsia-500 to-pink-500',
    glow: 'rgba(251,191,15,0.45)',
    title: 'Support & Retain',
    module: 'Support + White-label',
    desc: 'Keep customers happy with a built-in support desk. Resell the entire platform under your own brand to your own clients.',
    tools: ['Support Tickets', 'Admin Notifications', 'White-label Branding', 'Custom Domain', 'Reseller Portal'],
  },
];

/* ─────────────────────────────────────────────────────────────────────────────
   Pricing
───────────────────────────────────────────────────────────────────────────── */
/** Same left-sidebar module list as Choose Plan / backend DASHBOARD_MODULES_LIST */
const DASHBOARD_MODULES = [
  'Overview',
  'My Websites',
  'Create from Template',
  'Snag Builder',
  'AI Co-founder',
  'AI Tools',
  'Media Studio',
  'Bot Leads & Calls',
  'Voice Campaigns',
  'Domains',
  'Marketing Suite (Email, SMS, WhatsApp)',
  'AI SEO',
  'AI Ads',
  'CRM',
  'Automations',
  'Connections',
  'AI Studio',
  'Lead Scraper',
  'Platform Guide',
  'Support',
  'Snagcart Modules',
  'Settings',
];

const buildPlans = (_brand) => [
  {
    key: 'basic',
    name: 'Basic', price: 'Free', period: '',
    desc: 'You are selecting: Basic (Free) — all modules, teaser limits',
    features: [
      'All dashboard modules included (same list on every plan)',
      'Snag Builder + AI Co-founder (teaser: plan & draft, 1 run/day)',
      '1 website · branded subdomain',
      '25 lead + 50 AI credits',
      'Email / SMS / WhatsApp / Voice: BYOK · low send caps',
      'Domains: search only',
      '1 active automation · standard support',
    ],
    cta: 'Start free', highlight: false,
  },
  {
    key: 'starter',
    name: 'Starter', price: '$39', period: '/mo',
    desc: 'You are selecting: Starter ($39/mo) — all modules, business limits',
    features: [
      'All dashboard modules included (same list on every plan)',
      'Snag Builder + AI Co-founder (≈10 cycles/day · approve before send)',
      '3 websites · custom domain + domain wallet',
      '300 lead + 500 AI credits',
      'Email / SMS / WhatsApp / Voice: BYOK · normal caps',
      '10 active automations · Bot Leads & Voice ready',
      'Standard support',
    ],
    cta: 'Get started', highlight: true, badge: 'Most Popular',
  },
  {
    key: 'growth',
    name: 'Growth', price: '$149', period: '/mo',
    desc: 'You are selecting: Growth ($149/mo) — all modules, scale + agency',
    features: [
      'All dashboard modules included (same list on every plan)',
      'Snag Builder + AI Co-founder (always-on · multi-brand)',
      'Unlimited websites · custom domains + domain wallet',
      '1,500 lead + 2,500 AI credits',
      'Email / SMS / WhatsApp / Voice: BYOK · high caps',
      'Unlimited automations · agency white-label',
      'Priority support',
    ],
    cta: 'Get started', highlight: false,
  },
];

const PAYMENT_LAYERS = [
  { title: 'What you select', desc: 'Basic free · Starter $39 · Growth $149 — same modules (incl. AI Co-founder); different sites, cycles, credits, and caps.' },
  { title: 'Usage credits', desc: 'AI, leads, media & domains use included credits + optional packs.' },
  { title: 'Bring your own keys', desc: 'Email, SMS, WhatsApp, voice & store payments use your providers.' },
];

const catalogPlanToLanding = (key, entry) => {
  const priceUsd = Number(entry?.price_usd ?? 0);
  const isFree = !priceUsd;
  return {
    key,
    name: entry?.name || key,
    price: isFree ? 'Free' : `$${Math.round(priceUsd)}`,
    period: isFree ? '' : '/mo',
    desc: entry?.selection_label || entry?.description || '',
    features: entry?.highlights || [],
    cta: isFree ? 'Start free' : 'Get started',
    highlight: key === 'starter',
    badge: key === 'starter' ? 'Most Popular' : undefined,
  };
};

/* ─────────────────────────────────────────────────────────────────────────────
   AI Co-founder — "wow" value proposition + live activity showcase
   (Landing marketing element — activity feed is a realistic DEMO / mocked)
───────────────────────────────────────────────────────────────────────────── */
const COFOUNDER_FEED = [
  { tone: 'pending', icon: <Mail className="w-4 h-4" />, text: 'Drafted 3 follow-up emails to warm leads', status: 'Awaiting your approval' },
  { tone: 'done', icon: <Search className="w-4 h-4" />, text: 'Published SEO fix to /pricing', status: '+12 keywords now tracked' },
  { tone: 'scheduled', icon: <Megaphone className="w-4 h-4" />, text: 'Scheduled 4 Instagram posts for next week', status: 'Queued · you can edit' },
  { tone: 'done', icon: <Users className="w-4 h-4" />, text: 'Scored & sorted 47 new leads in CRM', status: '9 marked hot' },
  { tone: 'pending', icon: <Globe className="w-4 h-4" />, text: 'Rebuilt landing hero from your latest offer', status: 'Preview ready to review' },
  { tone: 'done', icon: <MessageCircle className="w-4 h-4" />, text: 'Replied to 6 WhatsApp enquiries', status: '2 booked a call' },
  { tone: 'scheduled', icon: <TrendingUp className="w-4 h-4" />, text: 'Launched a re-engagement campaign', status: '312 dormant contacts' },
  { tone: 'pending', icon: <FileText className="w-4 h-4" />, text: 'Wrote a blog post targeting "AI for agencies"', status: 'Awaiting your approval' },
];

const TONE_STYLES = {
  pending: { dot: '#fbbf0f', label: 'REVIEW', chip: 'bg-[#fbbf0f]/15 text-[#fbbf0f]' },
  done: { dot: '#34d399', label: 'DONE', chip: 'bg-emerald-400/15 text-emerald-300' },
  scheduled: { dot: '#60a5fa', label: 'QUEUED', chip: 'bg-sky-400/15 text-sky-300' },
};

const CofounderLiveShowcase = ({ brand = 'Snagcart', onCta = () => {} }) => {
  const [items, setItems] = useState(() =>
    [0, 1, 2].map((i) => ({ ...COFOUNDER_FEED[i], key: `seed-${i}` }))
  );

  useEffect(() => {
    let cursor = 3;
    const id = setInterval(() => {
      const next = COFOUNDER_FEED[cursor % COFOUNDER_FEED.length];
      cursor += 1;
      setItems((prev) => [{ ...next, key: `${next.text}-${Date.now()}` }, ...prev].slice(0, 4));
    }, 2600);
    return () => clearInterval(id);
  }, []);

  const metrics = [
    { icon: <Zap className="w-5 h-5" />, stat: '~10 loops/day', label: 'Runs growth cycles around the clock — even weekends' },
    { icon: <Layers className="w-5 h-5" />, stat: '1 brain · 15 tools', label: 'Replaces your CRM + marketing + builder + SEO stack' },
    { icon: <Shield className="w-5 h-5" />, stat: '0 rogue actions', label: 'You approve before anything goes live — always' },
    { icon: <TrendingUp className="w-5 h-5" />, stat: 'Compounds daily', label: 'Every loop learns from the last — your business gets sharper' },
  ];

  return (
    <section className="relative py-24 px-6 bg-[#16130d] overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{ backgroundImage: 'radial-gradient(rgba(251,191,15,0.9) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
      <div className="relative z-10 max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-center">

        {/* Left — value proposition */}
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fbbf0f] text-[#16130d] text-xs font-extrabold uppercase tracking-widest mb-6 shadow-[0_4px_0_0_rgba(0,0,0,0.35)]">
            <Bot className="w-4 h-4" strokeWidth={2.6} /> AI Co-founder · Never sleeps
          </motion.div>

          <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold tracking-tight leading-[1.05] text-white mb-5">
            AI agents that <span className="text-[#fbbf0f]">never sleep.</span><br />
            A human who&apos;s always in&nbsp;control.
          </motion.h2>

          <motion.p initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-lg text-stone-300 leading-relaxed max-w-xl mb-9">
            Hire a co-founder, not a tool. {brand}&apos;s AI operator plans, drafts and runs growth loops across
            leads, website, marketing, SEO and social — 24/7. Every action waits for your one-tap approval.
            Automation you can actually trust.
          </motion.p>

          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {metrics.map((m, i) => (
              <motion.div key={m.stat} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 hover:border-[#fbbf0f]/50 transition-colors">
                <div className="flex items-center gap-2 text-[#fbbf0f] mb-1.5">{m.icon}
                  <span className="text-lg font-heading font-extrabold text-white">{m.stat}</span>
                </div>
                <p className="text-sm text-stone-400 leading-snug">{m.label}</p>
              </motion.div>
            ))}
          </div>

          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} onClick={onCta}
            className="inline-flex items-center gap-2 bg-[#fbbf0f] text-[#16130d] rounded-full px-8 py-4 text-base font-bold shadow-[0_5px_0_0_rgba(251,191,15,0.35)] hover:shadow-[0_3px_0_0_rgba(251,191,15,0.35)] hover:translate-y-[2px] transition-all">
            Meet your AI Co-founder <ArrowRight className="w-5 h-5" />
          </motion.button>
        </div>

        {/* Right — live "working now" widget */}
        <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="rounded-3xl bg-[#1f1b12] border border-white/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/[0.03]">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
              </span>
              <span className="text-sm font-bold text-white">Co-founder is working</span>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Live demo</span>
          </div>

          <div className="p-4 space-y-3 min-h-[340px]">
            <AnimatePresence initial={false}>
              {items.map((it) => {
                const tone = TONE_STYLES[it.tone] || TONE_STYLES.done;
                return (
                  <motion.div key={it.key} layout
                    initial={{ opacity: 0, y: -14, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                    className="flex items-start gap-3 rounded-xl bg-white/[0.04] border border-white/10 p-3.5">
                    <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-[#fbbf0f]/15 text-[#fbbf0f] flex items-center justify-center">
                      {it.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: tone.dot }} />
                        <span className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${tone.chip}`}>{tone.label}</span>
                      </div>
                      <p className="text-sm text-white font-medium leading-snug">{it.text}</p>
                      <p className="text-xs text-stone-400 mt-0.5">{it.status}</p>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          <div className="px-5 py-3.5 border-t border-white/10 bg-white/[0.02] flex items-center justify-between">
            <span className="text-xs text-stone-500">While you were away · last 8 hours</span>
            <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-300">
              <Check className="w-3.5 h-3.5" /> 100% approved by you
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};


/* ─────────────────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────────────────── */
const Landing = ({
  brand = 'Snagcart',
  logoUrl = '/snagcart-logo.png',
  pricingPlans = null,
  authPath = '/auth',
  primaryColor = null,
} = {}) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeProduct, setActiveProduct] = useState(0);
  const [catalogPlans, setCatalogPlans] = useState(null);
  const [moduleChips, setModuleChips] = useState(DASHBOARD_MODULES);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const authForPlan = (planKey) => {
    if (!planKey) return authPath;
    const sep = authPath.includes('?') ? '&' : '?';
    return `${authPath}${sep}plan=${encodeURIComponent(planKey)}`;
  };

  const PRODUCTS = React.useMemo(() => buildProducts(brand), [brand]);
  const FLOW_STEPS = React.useMemo(() => buildFlowSteps(brand), [brand]);
  const PLANS = React.useMemo(() => {
    if (pricingPlans && pricingPlans.length > 0) {
      return pricingPlans.map((p) => ({
        key: p.key || '',
        name: p.name,
        price: p.price,
        period: '',
        desc: p.description || '',
        features: p.features || p.feature_notes || [],
        cta: p.cta || 'Get started',
        highlight: !!p.highlight,
        badge: p.highlight ? 'Most Popular' : undefined,
      }));
    }
    if (catalogPlans?.length) return catalogPlans;
    return buildPlans(brand);
  }, [brand, pricingPlans, catalogPlans]);

  useEffect(() => {
    if (pricingPlans?.length) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscription/catalog`);
        if (!res.ok) return;
        const cat = await res.json();
        if (cancelled) return;
        const plans = PLAN_ORDER
          .filter((k) => cat?.plans?.[k])
          .map((k) => catalogPlanToLanding(k, cat.plans[k]));
        if (plans.length) setCatalogPlans(plans);
        const mods = cat?.dashboard_modules || cat?.payment_model?.dashboard_modules;
        if (Array.isArray(mods) && mods.length) setModuleChips(mods);
      } catch { /* keep static fallbacks */ }
    })();
    return () => { cancelled = true; };
  }, [pricingPlans]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-cycle product tabs
  useEffect(() => {
    const t = setInterval(() => setActiveProduct(p => (p + 1) % PRODUCTS.length), 4000);
    return () => clearInterval(t);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="landing-clean min-h-screen overflow-x-hidden" data-testid="landing-clean">

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#fffaf7]/90 backdrop-blur-xl border-b border-stone-200/80 shadow-sm'
            : 'bg-transparent'
        }`}
        initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-[0_4px_0_0_rgba(22,19,13,0.9)]"
              style={{ background: '#16130d' }}
              data-testid="landing-logo">
              <Rocket className="w-5 h-5 text-[#fbbf0f] -rotate-45" strokeWidth={2.4} />
            </div>
            <span className="text-xl font-heading font-extrabold tracking-tight text-[#16130d]" data-testid="logo-text">{brand}</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/products" className="text-sm font-bold text-[#16130d] hover:text-black transition-colors">Products</Link>
            {[['flow', 'How It Works'], ['pricing', 'Pricing']].map(([id, label]) => (
              <button key={id} onClick={() => scrollTo(`${id}-section`)}
                className="text-sm font-bold text-[#16130d] hover:text-black transition-colors">{label}</button>
            ))}
            <Link to="/case-studies"
              className="text-sm font-bold text-[#16130d] hover:text-black transition-colors">Case Studies</Link>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(authPath)} data-testid="login-button"
              className="!text-[#16130d] font-bold hover:!text-black hover:bg-stone-100 text-sm">Login</Button>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              <Button onClick={() => navigate(authPath)} data-testid="get-started-button"
                className="!bg-[#FF6B4A] !text-white hover:!bg-[#f25536] rounded-full px-6 py-2 text-sm font-bold shadow-[0_8px_24px_rgba(255,107,74,0.28)]">
                Get started free
              </Button>
            </motion.div>
          </div>
          <button className="md:hidden text-stone-500 hover:text-stone-900" onClick={() => setMobileMenuOpen(v => !v)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}
              className="md:hidden bg-[#fffaf7] border-t border-stone-200 px-6 py-4 flex flex-col gap-4">
              <Link to="/products" onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-bold text-[#16130d] hover:text-black text-left">Products</Link>
              {[['flow', 'How It Works'], ['pricing', 'Pricing']].map(([id, label]) => (
                <button key={id} onClick={() => { setMobileMenuOpen(false); scrollTo(`${id}-section`); }}
                  className="text-sm font-bold text-[#16130d] hover:text-black text-left">{label}</button>
              ))}
              <Link to="/case-studies" onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-bold text-[#16130d] hover:text-black text-left">Case Studies</Link>
              <Button onClick={() => navigate(authPath)} className="!bg-[#FF6B4A] !text-white rounded-full font-bold w-full">
                Get started free
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col items-center justify-center pt-24 pb-16 px-6 overflow-hidden bg-[#fbbf0f]">
        <FloatingOrbs />
        <div className="pointer-events-none absolute inset-0"
          style={{ backgroundImage: 'radial-gradient(rgba(28,25,23,0.04) 1px, transparent 1px)', backgroundSize: '14px 14px' }} />

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 text-center max-w-5xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-[#16130d] text-[#16130d] text-sm font-semibold mb-8 shadow-[0_4px_0_0_#16130d]">
            <Rocket className="w-4 h-4 text-[#16130d] -rotate-45" strokeWidth={2.4} />
            AI agents that never sleep · 15 products · one subscription
          </motion.div>

          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-heading font-extrabold tracking-tight leading-[1.05] mb-6" data-testid="hero-title">
            <span className="block text-[#16130d]">{brand}</span>
            <span className="block text-[#16130d] mt-1 text-[0.55em] sm:text-[0.5em] font-bold tracking-tight">One platform for</span>
            <span className="block mt-2"><TypewriterText /></span>
          </motion.h1>

          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-base md:text-lg font-heading font-bold text-[#16130d] mb-7 -mt-1">
            The <span className="bg-[#16130d] text-[#fbbf0f] px-2 py-0.5 rounded-md">AI Business OS</span> for small teams &amp; agencies
          </motion.p>

          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={3}
            className="text-lg md:text-xl text-[#3a3327] max-w-2xl mx-auto mb-10 leading-relaxed font-medium" data-testid="hero-description">
            {brand} connects lead generation, website building, multi-channel marketing, AI automation and e-commerce
            into a single workspace — plus <span className="font-bold text-[#16130d]">AI agents that never sleep</span> to run
            growth loops 24/7 while you stay in control.
          </motion.p>

          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3}
            className="flex flex-wrap items-center justify-center gap-4 mb-16">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              <Button onClick={() => navigate(authPath)} data-testid="hero-cta-button"
                className="!bg-white !text-[#16130d] hover:!bg-white border-2 border-[#16130d] rounded-full px-8 py-4 text-base font-bold shadow-[0_5px_0_0_#16130d] hover:shadow-[0_3px_0_0_#16130d] hover:translate-y-[2px] transition-all">
                Start for free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
            <motion.button whileHover={{ scale: 1.03 }} onClick={() => scrollTo('flow-section')}
              className="flex items-center gap-2 text-[#16130d]/70 hover:text-[#16130d] text-base font-semibold transition-colors">
              See how it works <ChevronDown className="w-4 h-4" />
            </motion.button>
          </motion.div>

          {/* Animated feature explainer (drop-in “video”) */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4}>
            <FeatureExplainerVideo brand={brand} variant="light" />
          </motion.div>
        </motion.div>

        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-stone-400"
          animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <span className="text-xs">Scroll</span>
          <ChevronDown className="w-4 h-4" />
        </motion.div>
      </section>

      {/* ── AI Co-founder wow: value prop + live "working now" widget ─────── */}
      <CofounderLiveShowcase brand={brand} onCta={() => navigate(authPath)} />

      {/* ── Traction strip: trusted by + live metrics ────────────────────── */}
      <section className="py-14 px-6 border-y border-stone-200/80 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-10">
            <motion.div variants={fadeUp} custom={0} className="flex flex-col items-center gap-3 mb-8">
              <div className="inline-flex items-center gap-2 text-[#16130d]">
                <span className="flex">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-[#fbbf0f] text-[#fbbf0f]" />
                  ))}
                </span>
                <span className="text-sm font-bold">4.9/5</span>
                <span className="text-sm text-stone-400">from 2,400+ reviews</span>
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400">
                Trusted by 12,000+ growing businesses worldwide
              </p>
            </motion.div>

            <motion.div variants={fadeUp} custom={1}
              className="flex flex-wrap items-center justify-center gap-x-9 gap-y-4">
              {[
                { icon: <Megaphone className="w-5 h-5" />, label: 'Agencies', to: '/case-studies/agencies' },
                { icon: <ShoppingCart className="w-5 h-5" />, label: 'E-commerce', to: '/case-studies/ecommerce' },
                { icon: <Users className="w-5 h-5" />, label: 'Coaches & Creators', to: '/case-studies/coaches' },
                { icon: <Globe className="w-5 h-5" />, label: 'Local Services', to: '/case-studies/local-services' },
                { icon: <Zap className="w-5 h-5" />, label: 'Startups', to: '/case-studies/startups' },
                { icon: <Sparkles className="w-5 h-5" />, label: 'Restaurants', to: '/case-studies/restaurants' },
              ].map((b) => (
                <Link key={b.label} to={b.to}
                  className="flex items-center gap-2 text-stone-400 hover:text-[#16130d] transition-colors">
                  {b.icon}
                  <span className="text-base font-heading font-extrabold tracking-tight">{b.label}</span>
                </Link>
              ))}
            </motion.div>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-10 border-t border-stone-200/70">
            {[
              { to: 12400, prefix: '', suffix: '+', label: 'Active users' },
              { to: 8500, prefix: '', suffix: '+', label: 'Sites launched' },
              { to: 2, prefix: '', suffix: 'M+', label: 'Leads captured' },
              { to: 120, prefix: '$', suffix: 'M+', label: 'Processed for businesses' },
            ].map((s, i) => (
              <AnimatedSection key={s.label}>
                <motion.div variants={fadeUp} custom={i} className="text-center">
                  <p className="text-3xl md:text-4xl font-heading font-extrabold text-[#16130d]">
                    <Counter to={s.to} prefix={s.prefix} suffix={s.suffix} />
                  </p>
                  <p className="text-sm text-stone-500 mt-1">{s.label}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>


      {/* ── One app instead of ten (replaces the stack) ──────────────────── */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <motion.div variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#fdf3ce] border border-[#16130d]/15 text-[#16130d] text-xs font-bold mb-4">
              <Layers className="w-3.5 h-3.5" /> Why {brand}
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1}
              className="text-4xl md:text-5xl font-heading font-extrabold mb-4 text-[#16130d]">
              One app instead of ten
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-stone-500 max-w-2xl mx-auto">
              Most businesses juggle a pile of separate apps that don&apos;t talk to each other.
              {brand} does all those jobs in one place — so nothing falls through the cracks.
            </motion.p>
          </AnimatedSection>

          <div className="grid md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-4 items-center">
            {/* The usual setup */}
            <AnimatedSection>
              <motion.div variants={fadeUp} custom={0}
                className="rounded-3xl border border-stone-200 bg-[#faf7f2] p-7">
                <p className="text-sm font-bold text-stone-400 uppercase tracking-wide mb-5">The usual setup</p>
                <ul className="space-y-3">
                  {[
                    'A website builder',
                    'A lead-finding tool',
                    'A CRM & sales tool',
                    'An email / SMS / WhatsApp tool',
                    'An AI writing tool',
                    'An image & video tool',
                    'A chatbot / support tool',
                    'An automation tool',
                    'An online store tool',
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-stone-500">
                      <span className="w-5 h-5 rounded-full bg-stone-200 flex items-center justify-center shrink-0">
                        <X className="w-3 h-3 text-stone-500" strokeWidth={3} />
                      </span>
                      <span className="line-through decoration-stone-300">{t}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-sm text-stone-500">9+ logins · 9+ bills · nothing connected</p>
              </motion.div>
            </AnimatedSection>

            {/* Arrow */}
            <div className="flex md:flex-col items-center justify-center gap-2 text-[#16130d]">
              <div className="hidden md:flex w-12 h-12 rounded-full bg-[#fbbf0f] items-center justify-center shadow-[0_4px_0_0_#16130d]">
                <ArrowRight className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <div className="md:hidden w-11 h-11 rounded-full bg-[#fbbf0f] flex items-center justify-center shadow-[0_4px_0_0_#16130d] rotate-90">
                <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
              </div>
            </div>

            {/* With Snagcart */}
            <AnimatedSection>
              <motion.div variants={fadeUp} custom={1}
                className="rounded-3xl border-2 border-[#16130d] bg-[#fbbf0f] p-7 shadow-[0_10px_0_0_#16130d]">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#16130d' }}>
                    <Rocket className="w-5 h-5 text-[#fbbf0f] -rotate-45" strokeWidth={2.4} />
                  </div>
                  <p className="text-sm font-extrabold text-[#16130d] uppercase tracking-wide">With {brand}</p>
                </div>
                <ul className="space-y-3">
                  {[
                    'Build your website with AI',
                    'Find new customers',
                    'Manage leads & sales',
                    'Run Email, SMS & WhatsApp',
                    'Write content with AI',
                    'Create images & videos',
                    'Answer customers 24/7',
                    'Automate the busywork',
                    'Sell online',
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-[#16130d] font-semibold">
                      <span className="w-5 h-5 rounded-full bg-[#16130d] flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-[#fbbf0f]" strokeWidth={3.5} />
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-sm font-bold text-[#16130d]">1 login · 1 bill · everything connected</p>
              </motion.div>
            </AnimatedSection>
          </div>

          {/* ROI band — animated counters */}
          <AnimatedSection className="mt-12">
            <motion.div variants={fadeUp} custom={2}
              className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: 'You save', prefix: '$', to: 2000, suffix: '/mo', small: 'vs. paying for 10+ separate tools' },
                { label: 'You save', prefix: '', to: 20, suffix: '+ hrs', small: 'every week — no more copy-pasting between apps' },
                { label: 'Go live in', prefix: '', to: 5, suffix: ' min', small: 'not weeks — AI does the heavy lifting' },
              ].map((r) => (
                <div key={r.small} className="rounded-2xl border border-stone-200 bg-[#fffdf9] p-6 text-center">
                  <p className="text-xs font-bold uppercase tracking-wide text-stone-400 mb-1">{r.label}</p>
                  <p className="text-3xl md:text-4xl font-heading font-extrabold text-[#16130d]">
                    <Counter to={r.to} prefix={r.prefix} suffix={r.suffix} />
                  </p>
                  <p className="text-sm text-stone-500 mt-1">{r.small}</p>
                </div>
              ))}
            </motion.div>
          </AnimatedSection>
        </div>
      </section>


      {/* ── HOW IT WORKS — Animated Flow ────────────────────────────────── */}
      <section id="flow-section" className="py-28 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-20">
            <motion.div variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFF1EC] border border-[#FF6B4A]/25 text-[#E24B2D] text-xs font-medium mb-4">
              <Play className="w-3.5 h-3.5" /> The connected suite
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1}
              className="text-4xl md:text-5xl font-heading font-extrabold mb-4 text-stone-900">
              From first lead to loyal customer —
              <span className="block bg-gradient-to-r from-[#FF6B4A] to-[#14B8A6] bg-clip-text text-transparent">
                all in one platform
              </span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Every module in {brand} is designed to hand off seamlessly to the next. Here is the complete journey.
            </motion.p>
          </AnimatedSection>

          {/* Flow steps */}
          <div className="relative">
            {/* Vertical connector line */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent -translate-x-1/2" />

            <div className="space-y-8 lg:space-y-0">
              {FLOW_STEPS.map((step, i) => {
                const isLeft = i % 2 === 0;
                return (
                  <AnimatedSection key={step.step}>
                    <motion.div
                      variants={fadeUp} custom={0}
                      className={`lg:grid lg:grid-cols-2 lg:gap-16 items-center ${i > 0 ? 'lg:-mt-4' : ''}`}
                    >
                      {/* Content */}
                      <div className={`${isLeft ? 'lg:order-1' : 'lg:order-2'} mb-6 lg:mb-0`}>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-7 overflow-hidden"
                          style={{ boxShadow: `0 0 0 0 ${step.glow}` }}
                          whileHover={{ boxShadow: `0 0 50px 0 ${step.glow}` }}
                        >
                          {/* Gradient accent */}
                          <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${step.color}`} />

                          <div className="flex items-start gap-4 mb-4">
                            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center text-white shrink-0 shadow-lg`}>
                              {step.icon}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-bold bg-gradient-to-r ${step.color} bg-clip-text text-transparent`}>
                                  STEP {step.step}
                                </span>
                                <span className="text-xs text-zinc-500">·</span>
                                <span className="text-xs text-zinc-500">{step.module}</span>
                              </div>
                              <h3 className="text-xl font-heading font-bold">{step.title}</h3>
                            </div>
                          </div>

                          <p className="text-zinc-400 text-sm leading-relaxed mb-5">{step.desc}</p>

                          <div className="flex flex-wrap gap-2">
                            {step.tools.map(tool => (
                              <span key={tool}
                                className="text-[11px] px-2.5 py-1 rounded-full bg-white/8 border border-white/10 text-zinc-300">
                                {tool}
                              </span>
                            ))}
                          </div>
                        </motion.div>
                      </div>

                      {/* Center node */}
                      <div className={`hidden lg:flex ${isLeft ? 'lg:order-2' : 'lg:order-1'} items-center justify-center`}>
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          whileInView={{ scale: 1, opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                          className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white font-heading font-extrabold text-lg shadow-[0_8px_28px_rgba(255,107,74,0.25)] border-4 border-[#fffaf7]`}
                        >
                          {step.step}
                        </motion.div>
                      </div>
                    </motion.div>

                    {/* Arrow between steps */}
                    {i < FLOW_STEPS.length - 1 && (
                      <motion.div
                        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
                        viewport={{ once: true }} transition={{ delay: 0.4 }}
                        className="hidden lg:flex justify-center my-4"
                      >
                        <ChevronDown className="w-6 h-6 text-white/20" />
                      </motion.div>
                    )}
                  </AnimatedSection>
                );
              })}
            </div>
          </div>

          {/* Flow summary bar */}
          <AnimatedSection className="mt-20">
            <motion.div variants={fadeUp} custom={0}
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 overflow-x-auto">
              <p className="text-center text-sm text-zinc-500 mb-6 uppercase tracking-widest">The complete flow at a glance</p>
              <div className="flex items-center justify-between min-w-[600px] gap-2">
                {FLOW_STEPS.map((s, i) => (
                  <React.Fragment key={s.step}>
                    <div className="flex flex-col items-center gap-2 flex-1">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${s.color} flex items-center justify-center text-white shadow-lg`}>
                        {s.icon}
                      </div>
                      <p className="text-[11px] text-zinc-400 text-center leading-tight">{s.title}</p>
                    </div>
                    {i < FLOW_STEPS.length - 1 && (
                      <ChevronRight className="w-5 h-5 text-white/20 shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Products Section ─────────────────────────────────────────────── */}
      <section id="products-section" className="py-24 px-6 md:px-12 bg-gradient-to-b from-transparent via-violet-950/8 to-transparent">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <motion.div variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFF1EC] border border-[#FF6B4A]/25 text-[#E24B2D] text-xs font-medium mb-4">
              <Layers className="w-3.5 h-3.5" /> 15 products · everything included
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1}
              className="text-4xl md:text-5xl font-heading font-extrabold mb-4 text-stone-900">
              Every tool your business needs
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Stop paying for 14 different platforms. {brand} replaces them all — and connects them.
            </motion.p>
          </AnimatedSection>

          {/* Product tab selector */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {PRODUCTS.map((p, i) => (
              <motion.button
                key={p.id}
                onClick={() => setActiveProduct(i)}
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeProduct === i
                    ? `bg-gradient-to-r ${p.color} text-white shadow-lg`
                    : 'bg-white/5 border border-white/10 text-zinc-400 hover:text-white hover:bg-white/10'
                }`}
              >
                {p.title}
              </motion.button>
            ))}
          </div>

          {/* Active product detail card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeProduct}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-3xl border border-white/10 bg-white/[0.03] overflow-hidden mb-12"
              style={{ boxShadow: `0 0 60px 0 ${PRODUCTS[activeProduct].glow}` }}
            >
              <div className={`h-1 w-full bg-gradient-to-r ${PRODUCTS[activeProduct].color}`} />
              <div className="p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${PRODUCTS[activeProduct].color} flex items-center justify-center text-white shadow-xl`}>
                      {PRODUCTS[activeProduct].icon}
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">{PRODUCTS[activeProduct].tag}</span>
                      <h3 className="text-2xl font-heading font-bold">{PRODUCTS[activeProduct].title}</h3>
                      <p className={`text-sm font-medium bg-gradient-to-r ${PRODUCTS[activeProduct].color} bg-clip-text text-transparent`}>
                        {PRODUCTS[activeProduct].subtitle}
                      </p>
                    </div>
                  </div>
                  <p className="text-zinc-400 leading-relaxed mb-6">{PRODUCTS[activeProduct].desc}</p>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} className="flex flex-wrap gap-3">
                    <Button onClick={() => navigate(`/products/${PRODUCTS[activeProduct].id}`)}
                      className={`bg-gradient-to-r ${PRODUCTS[activeProduct].color} text-white rounded-full px-7 py-3 font-bold shadow-lg`}>
                      Explore {PRODUCTS[activeProduct].title} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                    <Button onClick={() => navigate(authPath)}
                      className="!bg-white !text-[#16130d] border-2 border-[#16130d] rounded-full px-6 py-3 font-bold">
                      Login
                    </Button>
                  </motion.div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-4">What's included</p>
                  <ul className="space-y-3">
                    {PRODUCTS[activeProduct].features.map((f, i) => (
                      <motion.li key={f}
                        initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.07 }}
                        className="flex items-start gap-3 text-sm text-zinc-300">
                        <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${PRODUCTS[activeProduct].color} flex items-center justify-center shrink-0 mt-0.5`}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        {f}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* All products grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {PRODUCTS.map((p, i) => (
              <AnimatedSection key={p.id}>
                <Link to={`/products/${p.id}`} className="block">
                  <motion.div
                    variants={fadeUp} custom={i % 5}
                    whileHover={{ y: -5, scale: 1.02 }}
                    onMouseEnter={() => setActiveProduct(i)}
                    className={`rounded-2xl border p-4 transition-all duration-200 ${
                      activeProduct === i
                        ? `border-white/20 bg-white/8 shadow-[0_0_25px_${p.glow}]`
                        : 'border-white/8 bg-white/[0.02] hover:border-white/15 hover:bg-white/5'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center text-white mb-3 shadow-md`}>
                      {p.icon}
                    </div>
                    <p className="text-sm font-semibold text-stone-900 leading-tight">{p.title}</p>
                    <p className="text-[11px] text-zinc-500 mt-1">{p.tag}</p>
                  </motion.div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── What the AI does for you (plain-language AI offerings) ────────── */}
      <section className="py-24 px-6 md:px-12 bg-[#fbbf0f]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <motion.div variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border-2 border-[#16130d] text-[#16130d] text-xs font-bold mb-4">
              <Sparkles className="w-3.5 h-3.5" /> Powered by AI
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1}
              className="text-4xl md:text-5xl font-heading font-extrabold mb-4 text-[#16130d]">
              What the AI actually does for you
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-[#3a3327] max-w-2xl mx-auto font-medium">
              No tech skills needed. Just tell {brand} what you want — the AI does the hard parts.
            </motion.p>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <Wand2 className="w-6 h-6" />, title: 'Builds your website', desc: 'Describe your business and AI creates the pages, text and images for you.' },
              { icon: <FileText className="w-6 h-6" />, title: 'Writes your content', desc: 'Blog posts, product descriptions, emails and captions in seconds.' },
              { icon: <Image className="w-6 h-6" />, title: 'Makes images & videos', desc: 'Product photos, ads, avatars and short videos — no designer needed.' },
              { icon: <MessageCircle className="w-6 h-6" />, title: 'Talks to your customers', desc: 'Chat and voice bots answer questions and book appointments 24/7.' },
              { icon: <Search className="w-6 h-6" />, title: 'Finds new customers', desc: 'Pulls business leads from maps and directories, ready to contact.' },
              { icon: <Megaphone className="w-6 h-6" />, title: 'Runs your marketing', desc: 'Writes and sends Email, SMS and WhatsApp campaigns for you.' },
              { icon: <TrendingUp className="w-6 h-6" />, title: 'Grows your traffic', desc: 'SEO and ad campaigns created and improved automatically.' },
              { icon: <Bot className="w-6 h-6" />, title: 'AI agents that never sleep', desc: 'AI Co-founder runs CRM, marketing, SEO and social loops 24/7 — you approve before anything goes live.' },
            ].map((c, i) => (
              <AnimatedSection key={c.title}>
                <motion.div variants={fadeUp} custom={i} whileHover={{ y: -6 }}
                  className="h-full rounded-3xl bg-white border border-[#16130d]/10 p-6 shadow-[0_6px_0_0_rgba(22,19,13,0.12)]">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#16130d' }}>
                    <span className="text-[#fbbf0f]">{c.icon}</span>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-[#16130d] mb-1.5">{c.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{c.desc}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Build agents by chat · connect apps · automate (AI Studio) ────── */}
      <section className="py-24 px-6 md:px-12 bg-[#16130d]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <motion.div variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#fbbf0f] text-[#16130d] text-xs font-bold mb-4">
              <Sparkles className="w-3.5 h-3.5" /> AI Studio · Automations · Connections
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1}
              className="text-4xl md:text-5xl font-heading font-extrabold mb-4 text-[#ffffff]">
              Describe it. Your AI builds the agent.
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-[#ffffff]/70 max-w-2xl mx-auto">
              No coding. No complicated flowcharts. Just chat with {brand} — it builds AI agents,
              connects all your apps, and runs the whole thing for you 24/7.
            </motion.p>
          </AnimatedSection>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Chat mockup */}
            <AnimatedSection>
              <motion.div variants={fadeUp} custom={0}
                className="rounded-3xl bg-[#211c14] border border-[#ffffff]/10 p-6 shadow-2xl">
                <div className="flex items-center gap-2 mb-5">
                  <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                  <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                  <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                  <span className="ml-2 text-xs text-[#ffffff]/40">{brand} · AI Studio</span>
                </div>

                <div className="flex justify-end mb-4">
                  <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-[#ffffff] text-[#16130d] px-4 py-3 text-sm font-medium">
                    Every time I get a new lead, message them on WhatsApp and book a call in my calendar.
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-[#fbbf0f]">
                    <Rocket className="w-4 h-4 text-[#16130d] -rotate-45" strokeWidth={2.4} />
                  </div>
                  <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-[#fbbf0f] text-[#16130d] px-4 py-3 text-sm">
                    <p className="font-bold mb-2">Done — your agent is live.</p>
                    <p className="mb-3">I built a “New Lead Follow-up” agent connected to WhatsApp and your Calendar. It runs 24/7.</p>
                    <div className="flex flex-wrap gap-1.5">
                      {['WhatsApp', 'Calendar', 'CRM', 'Trigger: New lead'].map((t) => (
                        <span key={t} className="text-[11px] font-semibold bg-[#16130d] text-[#fbbf0f] px-2 py-0.5 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-2 rounded-full border border-[#ffffff]/15 bg-[#16130d] px-4 py-2.5">
                  <span className="text-sm text-[#ffffff]/40 flex-1">Ask {brand} to build another agent…</span>
                  <span className="w-7 h-7 rounded-full bg-[#fbbf0f] flex items-center justify-center shrink-0">
                    <ArrowRight className="w-4 h-4 text-[#16130d]" strokeWidth={2.5} />
                  </span>
                </div>
              </motion.div>
            </AnimatedSection>

            {/* Highlights */}
            <AnimatedSection>
              <div className="space-y-4">
                {[
                  { icon: <MessageCircle className="w-6 h-6" />, title: 'Build any agent by chatting', desc: `Type what you want in plain English — ${brand} designs, wires up and launches the agent for you.` },
                  { icon: <Plug className="w-6 h-6" />, title: 'Connect all your apps', desc: 'Plug into WhatsApp, email, calendars, your CRM, payments and 50+ tools so everything works together.' },
                  { icon: <Workflow className="w-6 h-6" />, title: 'Automate any workflow', desc: 'Set simple "when this happens, do that" rules — no coding and no flowcharts to learn.' },
                  { icon: <Zap className="w-6 h-6" />, title: 'Runs 24/7, hands-free', desc: 'Your agents keep working while you sleep — following up, replying and getting things done.' },
                ].map((h, i) => (
                  <motion.div key={h.title} variants={fadeUp} custom={i}
                    className="flex items-start gap-4 rounded-2xl bg-[#ffffff]/[0.05] border border-[#ffffff]/10 p-5">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 bg-[#fbbf0f]">
                      <span className="text-[#16130d]">{h.icon}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-heading font-bold text-[#ffffff] mb-1">{h.title}</h3>
                      <p className="text-sm text-[#ffffff]/60 leading-relaxed">{h.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>


      {/* ── How we compare (generic, diplomatic) ─────────────────────────── */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <motion.div variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#fdf3ce] border border-[#16130d]/15 text-[#16130d] text-xs font-bold mb-4">
              <Check className="w-3.5 h-3.5" /> How we compare
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1}
              className="text-4xl md:text-5xl font-heading font-extrabold mb-4 text-[#16130d]">
              Everything together — not scattered across tools
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-stone-500 max-w-2xl mx-auto">
              Here&apos;s what you&apos;d normally buy several separate tools for, and how {brand} brings it all into one.
            </motion.p>
          </AnimatedSection>

          <AnimatedSection>
            <motion.div variants={fadeUp} custom={0} className="overflow-x-auto rounded-3xl border border-stone-200">
              <table className="w-full min-w-[640px] text-left border-collapse">
                <thead>
                  <tr className="bg-[#faf7f2]">
                    <th className="p-4 text-sm font-bold text-stone-500 w-[34%]">What you want to do</th>
                    <th className="p-4 text-center text-sm font-extrabold text-[#16130d] bg-[#fbbf0f]">{brand}</th>
                    <th className="p-4 text-center text-sm font-semibold text-stone-500">Website builders</th>
                    <th className="p-4 text-center text-sm font-semibold text-stone-500">CRM &amp; marketing tools</th>
                    <th className="p-4 text-center text-sm font-semibold text-stone-500">Lead-gen tools</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { job: 'Build a website with AI', snag: true, wb: true, crm: false, lg: false },
                    { job: 'Find new customer leads', snag: true, wb: false, crm: false, lg: true },
                    { job: 'Email, SMS & WhatsApp marketing', snag: true, wb: false, crm: true, lg: false },
                    { job: 'AI content, images & video', snag: true, wb: false, crm: false, lg: false },
                    { job: 'Chat & voice bots for support', snag: true, wb: false, crm: true, lg: false },
                    { job: 'Sell online (store + payments)', snag: true, wb: true, crm: false, lg: false },
                    { job: 'Automate your work', snag: true, wb: false, crm: true, lg: false },
                    { job: 'Use it under your own brand', snag: true, wb: false, crm: false, lg: false },
                    { job: 'One price for everything', snag: true, wb: false, crm: false, lg: false },
                  ].map((row, idx) => (
                    <tr key={row.job} className={idx % 2 ? 'bg-white' : 'bg-[#fffdf9]'}>
                      <td className="p-4 text-sm font-semibold text-[#16130d]">{row.job}</td>
                      {[
                        { on: row.snag, highlight: true },
                        { on: row.wb, highlight: false },
                        { on: row.crm, highlight: false },
                        { on: row.lg, highlight: false },
                      ].map((c, ci) => (
                        <td key={ci} className={`p-4 text-center ${c.highlight ? 'bg-[#fbbf0f]/15' : ''}`}>
                          {c.on ? (
                            <span className={`inline-flex w-6 h-6 rounded-full items-center justify-center ${c.highlight ? 'bg-[#16130d]' : 'bg-[#16130d]/10'}`}>
                              <Check className={`w-4 h-4 ${c.highlight ? 'text-[#fbbf0f]' : 'text-[#16130d]'}`} strokeWidth={3} />
                            </span>
                          ) : (
                            <span className="text-stone-300 text-lg font-bold">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </AnimatedSection>
          <p className="text-center text-xs text-stone-400 mt-4">
            Comparison shown by tool category, not specific brands. Every job above is included in {brand}.
          </p>
        </div>
      </section>


      {/* ── Pricing ──────────────────────────────────────────────────────── */}
      <section id="pricing-section" className="py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <motion.div variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFF1EC] border border-[#FF6B4A]/25 text-[#E24B2D] text-xs font-medium mb-4">
              <CreditCard className="w-3.5 h-3.5" /> Simple pricing
            </motion.div>
            <motion.h2 variants={fadeUp} custom={1}
              className="text-4xl md:text-5xl font-heading font-extrabold mb-4 text-stone-900">
              Start free. Scale as you grow.
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-zinc-400 text-lg max-w-2xl mx-auto">
              All three plans include every dashboard module — including Snag Builder and AI Co-founder. You select limits, credits, and support. Basic is free forever.
            </motion.p>
          </AnimatedSection>

          {!pricingPlans?.length && (
            <AnimatedSection className="mb-10">
              <motion.div variants={fadeUp} custom={0}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <p className="text-sm font-semibold text-stone-900 mb-3">Included on every plan (left sidebar)</p>
                <div className="flex flex-wrap gap-1.5">
                  {moduleChips.map((name) => (
                    <span key={name} className="rounded-md border border-white/10 bg-black/30 px-2.5 py-1 text-[11px] text-zinc-400">
                      {name}
                    </span>
                  ))}
                </div>
              </motion.div>
            </AnimatedSection>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map((plan, i) => (
              <AnimatedSection key={plan.name}>
                <motion.div variants={fadeUp} custom={i} whileHover={{ y: -8 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  className={`relative rounded-3xl p-8 h-full flex flex-col bg-white ${
                    plan.highlight
                      ? 'border-2 border-[#fbbf0f] shadow-[0_16px_48px_rgba(251,191,15,0.28)]'
                      : 'border border-stone-200'
                  }`}>
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#FF6B4A] text-white text-xs font-bold shadow-lg">
                      {plan.badge}
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="text-xl font-heading font-bold mb-1">{plan.name}</h3>
                    <p className="text-zinc-300 text-sm mb-1 font-medium">{plan.desc}</p>
                    <p className="text-zinc-500 text-xs mb-4">What you get with this selection</p>
                    <div className="flex items-end gap-1">
                      <span className="text-4xl font-heading font-extrabold">{plan.price}</span>
                      {plan.period ? <span className="text-zinc-500 mb-1">{plan.period}</span> : null}
                      {plan.price === '$39' || plan.price === '$149' ? (
                        <span className="text-zinc-600 text-xs mb-1.5 ml-1">· ₹{plan.price === '$39' ? '999' : '4,999'}/mo</span>
                      ) : null}
                    </div>
                  </div>
                  <ul className="space-y-3 flex-1 mb-8">
                    {plan.features.map(f => (
                      <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button onClick={() => navigate(authForPlan(plan.key))}
                      className={`w-full rounded-full font-bold py-3 ${
                        plan.highlight
                          ? '!bg-[#FF6B4A] !text-white hover:!bg-[#f25536]'
                          : '!bg-[#FFF1EC] !text-stone-800 hover:!bg-[#ffe4db] border border-[#FF6B4A]/30'
                      }`}
                      style={plan.highlight && primaryColor ? { background: primaryColor, color: '#fff' } : undefined}
                    >
                      {plan.cta}
                    </Button>
                  </motion.div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>

          {!pricingPlans?.length && (
            <AnimatedSection className="mt-12">
              <motion.div variants={fadeUp} custom={0}
                className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                {PAYMENT_LAYERS.map((layer) => (
                  <div key={layer.title}>
                    <p className="text-sm font-semibold text-stone-900 mb-1">{layer.title}</p>
                    <p className="text-xs text-zinc-500 leading-relaxed">{layer.desc}</p>
                  </div>
                ))}
              </motion.div>
            </AnimatedSection>
          )}
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────────── */}
      <section className="py-20 px-6 md:px-12 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <motion.h2 variants={fadeUp} custom={0}
              className="text-3xl md:text-4xl font-heading font-extrabold mb-3 text-stone-900">Loved by builders worldwide</motion.h2>
            <motion.div variants={fadeUp} custom={1} className="flex items-center justify-center gap-1 text-amber-400">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
              <span className="text-stone-500 text-sm ml-2">4.9 / 5 from 2,400+ reviews</span>
            </motion.div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { quote: `${brand} replaced 4 tools for us. We launched 3 client sites in a single week with the AI builder.`, name: 'Sarah K.', role: 'Agency Owner', avatar: 'SK' },
              { quote: 'The WhatsApp marketing suite alone is worth 10x the subscription. The warmer and broadcast features are incredible.', name: 'Rahul M.', role: 'E-commerce Founder', avatar: 'RM' },
              { quote: `White-labeling was seamless. Our clients think it's our own product — and the ${brand} ERP backend is a game changer.`, name: 'James T.', role: 'SaaS Reseller', avatar: 'JT' },
            ].map((t, i) => (
              <AnimatedSection key={t.name}>
                <motion.div variants={fadeUp} custom={i} whileHover={{ y: -4 }}
                  className="rounded-2xl bg-[#fffaf7] border border-stone-200 p-6 h-full flex flex-col">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                  </div>
                  <p className="text-stone-600 text-sm leading-relaxed flex-1 mb-6">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B4A] to-[#14B8A6] flex items-center justify-center text-sm font-bold text-white">{t.avatar}</div>
                    <div>
                      <p className="text-sm font-semibold text-stone-900">{t.name}</p>
                      <p className="text-xs text-stone-500">{t.role}</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="py-32 px-6 text-center relative overflow-hidden bg-[#fbbf0f]">
        <div className="pointer-events-none absolute inset-0">
          <motion.div className="absolute w-[900px] h-[450px] rounded-full bg-white/25 blur-[130px] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }} />
        </div>
        <AnimatedSection className="relative z-10 max-w-3xl mx-auto">
          <motion.h2 variants={fadeUp} custom={0}
            className="text-5xl md:text-6xl font-heading font-extrabold mb-6 leading-tight text-[#16130d]">
            Ready to build your{' '}
            <span className="text-[#16130d] underline decoration-white decoration-[6px] underline-offset-[6px]">
              AI-powered business?
            </span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={1} className="text-[#3a3327] text-xl mb-10 font-medium">
            Join 12,400+ builders who launched, grew and automated with {brand}.
          </motion.p>
          <motion.div variants={fadeUp} custom={2} className="flex flex-wrap items-center justify-center gap-4">
            <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.97 }}>
              <Button onClick={() => navigate(authPath)}
                className="!bg-white !text-[#16130d] hover:!bg-white border-2 border-[#16130d] rounded-full px-10 py-4 text-lg font-bold shadow-[0_5px_0_0_#16130d] hover:shadow-[0_3px_0_0_#16130d] hover:translate-y-[2px] transition-all">
                Get started free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
            <p className="text-[#3a3327] text-sm font-medium">No credit card required · Basic plan is free forever</p>
          </motion.div>
        </AnimatedSection>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="border-t border-stone-200 bg-white py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: '#16130d' }}>
              <Rocket className="w-4 h-4 text-[#fbbf0f] -rotate-45" strokeWidth={2.4} />
            </div>
            <span className="font-heading font-bold text-[#16130d]">{brand}</span>
          </div>
          <div className="flex flex-wrap items-center gap-6 text-sm text-stone-500">
            <Link to="/products" className="hover:text-stone-800 transition-colors">Products</Link>
            <Link to="/case-studies" className="hover:text-stone-800 transition-colors">Case Studies</Link>
            <a href="/subscription-plans" className="hover:text-stone-800 transition-colors">Pricing</a>
            <a href="/support" className="hover:text-stone-800 transition-colors">Support</a>
            <a href="/privacy" className="hover:text-stone-800 transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-stone-800 transition-colors">Terms</a>
            <a href="/refund" className="hover:text-stone-800 transition-colors">Refund</a>
            <a href="/return-policy" className="hover:text-stone-800 transition-colors">Return</a>
            <a href="/shipping-policy" className="hover:text-stone-800 transition-colors">Shipping</a>
          </div>
          <p className="text-stone-400 text-sm">© 2026 {brand}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
